<template>
  <div>
    <div v-for="(container, index) in containerPurchasingList" :key="index" class="mb-2">
      <div v-if="index > 0 && isContainerPODateInPast(containerPurchasingList[index-1]) && !isContainerPODateInPast(container)" class="my-4">
        <FormHorizontalRule />
        <h2>Future Purchasing</h2>
      </div>
      <ItemPanel icon="fa-container-storage" class="w-100" showTools>
        <template #tools>
          <router-link :to="{ path: '/purchasing/purchaseorders/new', query: { bulkData: generatePOData(container) } }" target="_blank" class="btn btn-primary">
            <i class="far fa-plus-square"></i> Create Purchase Order
          </router-link>
        </template>
        <template #title>
          <strong>Container #{{index + 1}} - {{ container.preferredSupplierName }}</strong>
        </template>
        <template #description>
          {{container.stockContainerName}}
        </template>
        <template #content>
          <ItemPanelData title="Supplier">
            <LinkContact :id="container.preferredSupplierId" :name="container.preferredSupplierName" />
          </ItemPanelData>
          <ItemPanelData title="Container">
            {{container.stockContainerName}}
          </ItemPanelData>
          <ItemPanelData title="Container Size">
            {{container.stockContainerBlockQuantity}} Blocks
          </ItemPanelData>
          <ItemPanelData title="Location">
            <LabelAccountLocationBadge :accountLocationId="container.accountLocationId" />
          </ItemPanelData>
          <ItemPanelData title="Supplier Lead Time">
            {{container.preferredSupplierLeadDays}} Days
          </ItemPanelData>
          <ItemPanelData title="Req. PO Creation">
            <span v-if="isContainerPODateInPast(container)" class="font-weight-bold">Now</span>
            <span v-else>{{formatDate(container.requiredPOCreationDate)}}</span>
          </ItemPanelData>
          <ItemPanelData title="Est. PO Arrival">
            {{formatDate(container.estimatedPOArrivalDate)}}
          </ItemPanelData>

          <FormHorizontalRule />
          <ContentTitle>Required Blocks</ContentTitle>
          <ContentSubTitle>Minimum number of blocks required to be ordered to meet the demand</ContentSubTitle>

          <div v-if="getRequiredBlockList(container).length > 0">
            <StaticDataTable smallRows :striped="false" :hover="false">
              <template #header>
                <tr>
                  <th width="30%">Stock</th>
                  <th width="10%">Glass Size</th>
                  <th width="15%">Min. Sheets Required</th>
                  <th width="10%">Blocks</th>
                  <th width="10%">Block Size</th>
                  <th width="10%">Total Sheets</th>
                  <th width="15%">Info</th>
                </tr>
              </template>
              <template #body>
                <tr v-for="(blockInfo, itemIndex) in getRequiredBlockList(container)" :key="itemIndex">
                  <td><LinkStock :id="blockInfo.stockId" :name="blockInfo.stockName" /></td>
                  <td>{{blockInfo.height}} x {{blockInfo.width}}</td>
                  <td>{{blockInfo.minSheetsToOrder}}</td>
                  <td>
                    {{blockInfo.quantity}} <a :class="['order-item-editable-button', 'ml-2', blockInfo.isOverride ? 'overridden-blocks' : '']" @click.prevent="editBlockOverride($event, container, blockInfo, false)"><i class="far fa-pencil"></i></a>
                  </td>
                  <td>{{blockInfo.blockSize}}</td>
                  <td>{{blockInfo.totalSheets}}</td>
                  <td>
                    <FormButton class="btn-xs btn-default" @click.prevent="displayStockLevelChart($event, container, blockInfo)">
                      <i class="far fa-chart-line"></i> Chart
                    </FormButton>
                  </td>
                </tr>
                <tr v-if="getTopUpBlockList(container).length == 0">
                  <td class="font-weight-bold" colspan="3">Totals</td>
                  <td class="font-weight-bold">{{getContainerTotalBlockCount(container)}} / {{container.stockContainerBlockQuantity}}</td>
                  <td></td>
                  <td class="font-weight-bold">{{getContainerTotalSheetCount(container)}}</td>
                  <td></td>
                </tr>
              </template>
            </StaticDataTable>
          </div>

          <div v-if="getTopUpBlockList(container).length > 0">
            <ContentTitle>Top Up Blocks</ContentTitle>
            <ContentSubTitle>Additional blocks required to meet container capacity, based on next-required items</ContentSubTitle>
            <StaticDataTable smallRows :striped="false" :hover="false">
              <template #header>
                <tr>
                  <th width="30%">Stock</th>
                  <th width="10%">Glass Size</th>
                  <th width="15%"> </th>
                  <th width="10%">Top Up Blocks</th>
                  <th width="10%">Block Size</th>
                  <th width="10%">Total Sheets</th>
                  <th width="15%">Info</th>
                </tr>
              </template>
              <template #body>
                <tr v-for="(blockInfo, itemIndex) in getTopUpBlockList(container)" :key="itemIndex">
                  <td><LinkStock :id="blockInfo.stockId" :name="blockInfo.stockName" /></td>
                  <td>{{blockInfo.height}} x {{blockInfo.width}}</td>
                  <td>-</td>
                  <td>
                    {{blockInfo.quantity}} <a :class="['order-item-editable-button', 'ml-2', blockInfo.isOverride ? 'overridden-blocks' : '']" @click.prevent="editBlockOverride($event, container, blockInfo, true)"><i class="far fa-pencil"></i></a>
                  </td>
                  <td>{{blockInfo.blockSize}}</td>
                  <td>{{blockInfo.totalSheets}}</td>
                  <td>
                    <FormButton class="btn-xs btn-default" @click.prevent="displayStockLevelChart($event, container, blockInfo)">
                      <i class="far fa-chart-line"></i> Chart
                    </FormButton>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold" colspan="3">Totals</td>
                  <td class="font-weight-bold">{{getContainerTotalBlockCount(container)}} / {{container.stockContainerBlockQuantity}}</td>
                  <td></td>
                  <td class="font-weight-bold">{{getContainerTotalSheetCount(container)}}</td>
                  <td></td>
                </tr>
              </template>
            </StaticDataTable>
          </div>

          <FormButton class="btn-xs btn-default mb-2" @click="displayAddContainerItemPopover($event, container)">
            <i class="far fa-plus"></i> Add Item
          </FormButton>

        </template>
      </ItemPanel>
    </div>

    <Popover ref="stockLevelChartPopover" placement="right" :showHeader="true" :headerTitle="selectedChartInfo.title" sizeClass="popover-xxlarge" :scrollable="true">
      <div>
        <InventoryStockLevelChartWidget
          v-if="selectedChartInfo.stockId"
          :embedded="true"
          :displayFilter="false"
          :popupEnabled="false"
          :exportEnabled="false"
          :stockFilterEnabled="false"
          :gridHeight="330"
          :glassSizeId="selectedChartInfo.glassSizeId"
          :accountLocationId="selectedChartInfo.accountLocationId"
          :stockId="selectedChartInfo.stockId"
          :startDate="selectedChartInfo.startDate"
          :endDate="selectedChartInfo.endDate"
          :plannedPurchaseOrders="selectedChartInfo.plannedPurchaseOrders"
        />
      </div>
    </Popover>

    <EditValuePopover
      ref="editValuePopover"
      title="Override Quantity"
      type="number"
      :removeButtonEnabled="true"
      removeButtonText="Reset"
      applyButtonText="Override"
      @apply="applyBlockItemOverride"
      @remove="removeBlockItemOverride"
      />

    <Popover ref="newContainerItem" placement="top" :showHeader="true" headerTitle="Add Item to Container" sizeClass="popover-xlarge">
      <div class="p-2">
        <FormGroup>
          <FormRowFull label="Stock">
            <FormInputStockCode placeholder="Stock Item" :stockType="InventoryStockTypes.glass" v-model="customAddItemInfo.stockId" />
          </FormRowFull>
          <FormRowFull label="Glass Size">
            <FormSelect v-model="customAddItemInfo.glassSizeId" :options="customAddItemInfo.glassSizeLookup" />
          </FormRowFull>
          <FormRowFull label="Account Location">
            <FormStaticLabel><LabelAccountLocationBadge :accountLocationId="customAddItemInfo.accountLocationId" /></FormStaticLabel>
          </FormRowFull>
          <FormRowFull label="Info" v-if="customAddItemInfo.stockId && customAddItemInfo.glassSizeId">
            <FormStaticLabel>
              Preferred Supplier: {{customAddInfoPreferredSupplier}}<br />
              Default Container: {{customAddInfoStockContainer}}
            </FormStaticLabel>
          </FormRowFull>
          <FormRowFull label="Block Quantity">
            <FormInputText type="number" placeholder="Block Quantity" v-model="customAddItemInfo.blockQuantity" />
          </FormRowFull>

          <FormButton class="btn-block btn-primary mt-2" @click="addContainerItem" :disabled="isAddItemDisabled">
            <i class="far fa-plus-square"></i> Add
          </FormButton>

        </FormGroup>
      </div>
    </Popover>
  </div>
</template>


<style scoped>
.overridden-blocks {
  font-weight: bold !important;
  color: #d9534f !important;
}
</style>


<script>
import _ from 'lodash'
import moment from 'moment'

import Popover from '@/components/popover'
import InventoryStockLevelChartWidget from '@/modules/intelligence/dashboardWidgets/inventoryStockLevelChartWidget'
import EditValuePopover from '@/components/editValuePopover'
import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'

import { InventoryStockTypes } from '@/store/types'

export default {
  name: 'PurchaseOrderContainerPurchaseList',
  components: {
    Popover,
    InventoryStockLevelChartWidget,
    EditValuePopover,
    FormInputStockCode
  },
  props: {
    containerPurchasingList: Array,
    usageStartDate: {
      type: String,
      required: false
    },
    usageEndDate: {
      type: String,
      required: false
    },
    incomingStartDate: {
      type: String,
      required: false
    },
    incomingEndDate: {
      type: String,
      required: false
    },
    containerOverrideList: Array
  },
  data() {
    return {
      selectedChartInfo: {},
      overrideList: _.cloneDeep(this.containerOverrideList || []),
      selectedOverrideItem: null,
      selectedContainer: null,
      customAddItemInfo: {
        contactId: null,
        stockContainerId: null,
        stockId: null,
        glassSizeId: null,
        accountLocationId: null,
        blockQuantity: null,
        glassSizeLookup: []
      }
    }
  },
  computed: {
    isAddItemDisabled() {
      return !this.customAddItemInfo.stockId || !this.customAddItemInfo.glassSizeId || !this.customAddItemInfo.blockQuantity
    },
    customAddInfoPreferredSupplier() {
      var selectedSize = this.customAddItemInfo.glassSizeLookup.find(size => size.value == parseInt(this.customAddItemInfo.glassSizeId))
      if(!selectedSize)
        return 'N/A'

      if(selectedSize.preferredSupplier)
        return selectedSize.preferredSupplier.name
      return 'None'
    },
    customAddInfoStockContainer() {
      var selectedSize = this.customAddItemInfo.glassSizeLookup.find(size => size.value == parseInt(this.customAddItemInfo.glassSizeId))
      if(!selectedSize)
        return 'N/A'

      if(selectedSize.stockContainer)
        return selectedSize.stockContainer.name
      return 'None'
    }
  },
  watch: {
    containerOverrideList: {
      handler(newVal) {
        this.overrideList = _.cloneDeep(newVal)
      },
      deep: true
    },
    'customAddItemInfo.stockId'(newVal) {
      this.addInfoStockUpdated()
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
  },
  methods: {
    generatePOData(container) {
      var date = moment().local().add(container.preferredSupplierLeadDays, 'days')
      
      if(moment(container.estimatedPOArrivalDate).isAfter(date))
        date = moment(container.estimatedPOArrivalDate).local()

      // Sort blocks by required first, topup second
      var outputBlocks = _.sortBy(container.requiredBlocks, block => {
        return block.isTopUpItem ? 1 : 0
      })

      return JSON.stringify({
        stockContainerId: container.stockContainerId,
        contactId: container.preferredSupplierId,
        accountLocationId: container.accountLocationId,
        notes: container.purchaseOrderNotes || '',
        date: date.toISOString(),
        items: outputBlocks.map(block => ({
          // Abbreviated keys to reduce URL length
          s: block.stockId,
          q: block.quantity,
          gsid: block.glassSizeId
        }))
      })
    },
    getContainerTotalBlockCount(container) {
      return _.sumBy(container.requiredBlocks, block => block.quantity)
    },
    getContainerTotalSheetCount(container) {
      return _.sumBy(container.requiredBlocks, block => {
        let itemInfo = container.itemList.find(item => item.stockId === block.stockId && item.mainGlassSizeId === block.glassSizeId)
        if(!itemInfo)
          return 0

        return itemInfo.blockSize * block.quantity
      })
    },
    getRequiredBlockList(container) {
      return _.compact(container.requiredBlocks.map(block => {
        let itemInfo = container.itemList.find(item => item.stockId === block.stockId && item.mainGlassSizeId === block.glassSizeId)
        if(!itemInfo)
          return null

        if(block.isTopUpItem)
          return null

        return {
          stockId: block.stockId,
          glassSizeId: block.glassSizeId,
          quantity: block.quantity,
          stockName: `${itemInfo.stockCode} - ${itemInfo.stockName}`,
          width: itemInfo.width,
          height: itemInfo.height,
          blockSize: itemInfo.blockSize,
          totalSheets: itemInfo.blockSize * block.quantity,
          isOverride: block.isOverride,
          minSheetsToOrder: block.minSheetsToOrder
        }
      }))
    },
    getTopUpBlockList(container) {
      return _.compact(container.requiredBlocks.map(block => {
        let itemInfo = container.itemList.find(item => item.stockId === block.stockId && item.mainGlassSizeId === block.glassSizeId)
        if(!itemInfo)
          return null

        if(!block.isTopUpItem)
          return null

        return {
          stockId: block.stockId,
          glassSizeId: block.glassSizeId,
          quantity: block.quantity,
          stockName: `${itemInfo.stockCode} - ${itemInfo.stockName}`,
          width: itemInfo.width,
          height: itemInfo.height,
          blockSize: itemInfo.blockSize,
          totalSheets: itemInfo.blockSize * block.quantity,
          isOverride: block.isOverride,
          minSheetsToOrder: block.minSheetsToOrder
        }
      }))
    },
    displayStockLevelChart(ev, container, blockInfo) {
      this.selectedChartInfo = {
        stockId: blockInfo.stockId,
        glassSizeId: blockInfo.glassSizeId,
        accountLocationId: container.accountLocationId,
        title: `Stock Level Chart for ${blockInfo.stockName} - ${blockInfo.height} x ${blockInfo.width}`,
        startDate: moment(this.usageStartDate).toISOString(), // Start of the usage
        endDate: moment(this.incomingEndDate).toISOString(),   // End of the Incoming
        plannedPurchaseOrders: []
      }

      // Show ALL purchase order containers in the chart
      let fullPOList = []

      this.containerPurchasingList.forEach(otherContainer => {
        if(otherContainer.accountLocationId !== container.accountLocationId)
          return

        let totalSheets = 0
        otherContainer.requiredBlocks.forEach(block => {
          if(block.stockId === blockInfo.stockId && block.glassSizeId === blockInfo.glassSizeId) {
            totalSheets += block.quantity * blockInfo.blockSize
          }
        })

        if(totalSheets > 0) {
          fullPOList.push({
            stockId: blockInfo.stockId,
            glassSizeId: blockInfo.glassSizeId,
            date: moment(otherContainer.requiredPOCreationDate).add(otherContainer.preferredSupplierLeadDays, 'days').toISOString(),
            totalSheets: totalSheets,
            highlight: otherContainer.id === container.id
          })
        }
      })


      let poCreationDate = moment(container.requiredPOCreationDate)
      // Cannot order in the past
      if(poCreationDate.isBefore(moment())) {
        poCreationDate = moment()
      }

      // Planned purchase list
      this.selectedChartInfo.plannedPurchaseOrders = fullPOList

      this.$refs.stockLevelChartPopover.displayFromElement(ev.target)
    },
    isContainerPODateInPast(container) {
      return moment(container.requiredPOCreationDate).local().isSameOrBefore(moment(), 'day')
    },
    editBlockOverride(event, container, blockInfo, isTopUpItem = false) {
      this.selectedOverrideItem = _.clone(blockInfo)
      this.selectedOverrideItem.isTopUpItem = isTopUpItem
      this.selectedOverrideItem.contactId = container.preferredSupplierId
      this.selectedOverrideItem.stockContainerId = container.stockContainerId
      this.selectedOverrideItem.accountLocationId = container.accountLocationId
      this.selectedOverrideItem.requiredPOCreationDate = container.requiredPOCreationDate
      this.selectedOverrideItem.estimatedPOArrivalDate = container.estimatedPOArrivalDate
      this.selectedOverrideItem.minSheetsToOrder = blockInfo.minSheetsToOrder

      this.$refs.editValuePopover.displayFromElement(event.target, blockInfo.quantity)
    },
    applyBlockItemOverride(value) {
      value = parseInt(value)

      var curOverride = this.overrideList.find(override => {
        return override.stockId == this.selectedOverrideItem.stockId && 
          override.glassSizeId == this.selectedOverrideItem.glassSizeId && 
          override.isTopUpItem == this.selectedOverrideItem.isTopUpItem &&
          override.stockContainerId == this.selectedOverrideItem.stockContainerId &&
          override.contactId == this.selectedOverrideItem.contactId &&
          override.accountLocationId == this.selectedOverrideItem.accountLocationId && 
          override.requiredPOCreationDate == this.selectedOverrideItem.requiredPOCreationDate &&
          override.estimatedPOArrivalDate == this.selectedOverrideItem.estimatedPOArrivalDate
      })

      if(curOverride) {
        curOverride.quantity = value
      }
      else {
        this.overrideList.push({
          stockId: this.selectedOverrideItem.stockId,
          glassSizeId: this.selectedOverrideItem.glassSizeId,
          isTopUpItem: this.selectedOverrideItem.isTopUpItem,
          stockContainerId: this.selectedOverrideItem.stockContainerId,
          contactId: this.selectedOverrideItem.contactId,
          accountLocationId: this.selectedOverrideItem.accountLocationId,
          requiredPOCreationDate: this.selectedOverrideItem.requiredPOCreationDate,
          estimatedPOArrivalDate: this.selectedOverrideItem.estimatedPOArrivalDate,
          minSheetsToOrder: this.selectedOverrideItem.minSheetsToOrder,
          quantity: value
        })
      }

      this.$emit('updateOverrides', this.overrideList)
    },
    removeBlockItemOverride() {
      this.overrideList = this.overrideList.filter(override => {
        return override.stockId != this.selectedOverrideItem.stockId || 
          override.glassSizeId != this.selectedOverrideItem.glassSizeId ||
          override.isTopUpItem != this.selectedOverrideItem.isTopUpItem ||
          override.stockContainerId != this.selectedOverrideItem.stockContainerId ||
          override.contactId != this.selectedOverrideItem.contactId ||
          override.accountLocationId != this.selectedOverrideItem.accountLocationId
      })

      this.$emit('updateOverrides', this.overrideList)
    },

    displayAddContainerItemPopover(ev, container) {
      this.customAddItemInfo.contactId = container.preferredSupplierId
      this.customAddItemInfo.stockContainerId = container.stockContainerId
      this.customAddItemInfo.accountLocationId = container.accountLocationId

      // Reset settings
      this.customAddItemInfo.stockId = null
      this.customAddItemInfo.glassSizeId = null
      this.customAddItemInfo.blockQuantity = 0
      this.customAddItemInfo.glassSizeLookup = []

      this.$refs.newContainerItem.displayFromElement(ev.target)
    },
    addContainerItem() {
      // Find the container

      // Add override list
      this.overrideList.push({
        contactId: parseInt(this.customAddItemInfo.contactId),
        stockContainerId: parseInt(this.customAddItemInfo.stockContainerId),
        stockId: parseInt(this.customAddItemInfo.stockId),
        glassSizeId: parseInt(this.customAddItemInfo.glassSizeId),
        accountLocationId: parseInt(this.customAddItemInfo.accountLocationId),
        isTopUpItem: false,
        quantity: parseInt(this.customAddItemInfo.blockQuantity)
      })

      this.$refs.newContainerItem.hidePopover()

      this.$emit('updateOverrides', this.overrideList)
    },
    addInfoStockUpdated() {
      this.customAddItemInfo.glassSizeId = null
      this.customAddItemInfo.glassSizeLookup = []

      if(!this.customAddItemInfo.stockId)
        return

      // Load the stock info & grab the available glass size list
      this.$apiService.inventoryService.getStock(this.customAddItemInfo.stockId)
      .then(response => {
        let stockInfo = response.data
        
        this.customAddItemInfo.glassSizeLookup = _.concat([{
          name: 'None', value: 0
        }], stockInfo.glassSizeList.filter(glassSize => glassSize.isActive).map(size => {
          return {
            name: `${size.height} x ${size.width}`,
            value: size.id,
            preferredSupplier: size.preferredSupplier,
            stockContainer: size.stockContainer
          }
        }))
      })
      .catch(err => {
        console.error(err)
      })
    }
  }
}
</script>